import { Badge, Skeleton } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  BankIdentityInfosFragment,
  BankIdentityRole,
  IdentityType,
  IdentityVerificationStatus,
  useGetSpvDraftQuery,
} from '@blockpulse3/graphql/hooks';
import { CardIdentity, ErrorQueryCard } from '@blockpulse3/ui/commons';

import { getVerificationStatusBadgeContent } from './utils/getVerificationStatusBadgeContent';

interface BankIdentitiesListProps {
  bankIdentities: BankIdentityInfosFragment[];
}

export const SPVDraftIdentitiesList = ({
  bankIdentities,
}: BankIdentitiesListProps): JSX.Element | null => {
  const i18nSPVIdentity = useTranslations('SPVMatriculationIdentity.Badge');
  const i18nRoleValues = useTranslations('RoleValues');

  const { id = '' } = useParams();

  const { data, error, loading } = useGetSpvDraftQuery({ variables: { id } });

  if (loading) {
    return <Skeleton h="50px" w="full" />;
  }

  if (error) {
    return <ErrorQueryCard h="50px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="50px" />;
  }

  if (!bankIdentities?.length) {
    return null;
  }

  const draft = data.spvDraft;

  /* ** Return full name of the identity ** */
  const getIdentityName = (bankIdentity: BankIdentityInfosFragment): string => {
    const { identity } = bankIdentity;
    if (bankIdentity.role === BankIdentityRole.SPV) {
      return draft.content.name;
    }
    if (identity.type === IdentityType.COMPANY) {
      return identity.companyIdentity?.name || '';
    }
    return `${identity.individualIdentity?.firstName} ${identity.individualIdentity?.lastName}`;
  };

  /* ** Used to change card identity icon ** */
  const getBankIdentityType = (bankIdentity: BankIdentityInfosFragment): IdentityType => {
    if (bankIdentity.role === BankIdentityRole.SPV) {
      return IdentityType.COMPANY;
    }
    return bankIdentity.identity.type;
  };

  const handleIdentityClick = (bankIdentity: BankIdentityInfosFragment): void => {
    const { onboardingLink = null } = bankIdentity;
    if (
      onboardingLink &&
      [IdentityVerificationStatus.NONE, IdentityVerificationStatus.REFUSED].includes(
        bankIdentity.verificationStatus,
      )
    ) {
      window.location.href = onboardingLink;
    }
  };

  return (
    <>
      {bankIdentities.map((bankIdentity) => {
        const verificationStatus = bankIdentity.verificationStatus;
        const { color, translationKey } = getVerificationStatusBadgeContent(verificationStatus);
        return (
          <CardIdentity
            key={bankIdentity.id}
            rightNode={<Badge colorScheme={color}>{i18nSPVIdentity(translationKey)}</Badge>}
            subTitle={getIdentityName(bankIdentity)}
            title={i18nRoleValues(bankIdentity.role as keyof IntlMessages['RoleValues'])}
            type={getBankIdentityType(bankIdentity)}
            src={
              bankIdentity.role === BankIdentityRole.SPV
                ? undefined
                : bankIdentity.identity.profilePicture
            }
            onClick={(): void => handleIdentityClick(bankIdentity)}
          />
        );
      })}
    </>
  );
};
